/**
 * TURNING DATABASE COLUMNS INTO SENTENCES A PLAYER WOULD SAY.
 *
 * The staff inspector prints a view's columns verbatim, and it is right to: an admin comparing rows
 * needs `minecraft:overworld`, `READY` and an ISO instant, exactly as Postgres holds them. The
 * player reading `/me` is the opposite reader - they know the Overworld, they have never typed a
 * namespaced identifier, and "8/3/2026, 12:41:00 PM" is a worse answer than "today at 12:41" to the
 * question they actually asked, which is whether the server remembers them.
 *
 * EVERY FUNCTION HERE IS TOTAL AND NULL-TOLERANT. The row types say `x` is a number and `created_at`
 * is a string, and PostgREST does send them - but this page renders whatever the view returned, and
 * a partial fixture row or a column added tomorrow must produce a missing line rather than
 * "undefined" or a thrown render. So every input is accepted as `unknown`-ish and every function
 * returns `null` for "nothing worth printing", which the callers render as an absent element.
 *
 * NOTHING HERE HIDES AN IDENTIFIER FROM SOMEBODY WHO WANTS IT. Where a friendly label replaces a
 * technical one, the caller keeps the original in a `title` attribute - so `Overworld` is what the
 * page says and `minecraft:overworld` is what a curious reader (or a support ticket) can still get.
 */

/** The three vanilla dimensions, which are the ones with a name in English rather than an id. */
const VANILLA_DIMENSIONS: Readonly<Record<string, string>> = {
  'minecraft:overworld': 'Overworld',
  'minecraft:the_nether': 'The Nether',
  'minecraft:the_end': 'The End',
};

/**
 * `cobblemondimensions:sb_<uuid>` is a secret base's pocket world (see `PocketWorlds`), and its
 * path is a UUID - which titlecased would be a wall of hex. It gets a name instead.
 */
const POCKET_WORLD_PREFIX = 'sb_';

/**
 * `minecraft:overworld` -> `Overworld`, `cobblemon:safari` -> `Safari`, a base's pocket world ->
 * `A secret base`. An unknown namespace keeps its path titlecased, which is the honest guess: it is
 * what a modpack's own dimension usually reads like once the namespace is dropped.
 */
export function dimensionLabel(id: unknown): string | null {
  if (typeof id !== 'string' || id.trim() === '') return null;
  const known = VANILLA_DIMENSIONS[id];
  if (known !== undefined) return known;
  const path = id.includes(':') ? (id.split(':')[1] ?? id) : id;
  if (path.startsWith(POCKET_WORLD_PREFIX)) return 'A secret base';
  return titleCase(path);
}

/** `cobblemon:rare_candy` -> `Rare candy`. Item ids are namespaced snake_case, uniformly. */
export function itemLabel(id: unknown): string | null {
  if (typeof id !== 'string' || id.trim() === '') return null;
  const path = id.includes(':') ? (id.split(':')[1] ?? id) : id;
  return titleCase(path);
}

/** `READY` -> `Ready`, `awaiting_world` -> `Awaiting world`. */
export function titleCase(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const words = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * `128.5, 71, -344.5` -> `128 71 -344`.
 *
 * Rounded, because a player standing in a block does not care about the fractional part of it, and
 * because three integers are scannable where three decimals are not. Rendered in the mono face by
 * the caller so the columns line up between homes.
 */
export function coordinates(x: unknown, y: unknown, z: unknown): string | null {
  if (typeof x !== 'number' || typeof y !== 'number' || typeof z !== 'number') return null;
  return `${Math.round(x)} ${Math.round(y)} ${Math.round(z)}`;
}

/** The two-letter glyph on a mailbox row: `rare_candy` -> `RC`, `diamond` -> `DI`. */
export function itemInitials(id: unknown): string {
  if (typeof id !== 'string') return '??';
  const path = id.includes(':') ? (id.split(':')[1] ?? id) : id;
  const words = path.split(/[_-]+/).filter((word) => word.length > 0);
  if (words.length >= 2) return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase();
  return (path.slice(0, 2) || '??').toUpperCase();
}

/**
 * An instant as somebody would say it out loud: `today at 14:32`, `yesterday at 09:02`,
 * `Tuesday at 21:15` inside the last week, and a plain date beyond it.
 *
 * WHY NOT "3 hours ago". Elapsed-time wording has to be re-rendered to stay true, and this page
 * polls every thirty seconds rather than every second, so "a minute ago" would sit there for half a
 * minute being wrong. A wall-clock time is right for as long as it is on screen.
 *
 * The time itself is formatted by the platform (`toLocaleTimeString`), so a reader whose locale is
 * 12-hour gets 2:32 PM and a 24-hour locale gets 14:32; only the English scaffolding is ours. `now`
 * is injectable so the tests are not a clock.
 */
export function friendlyInstant(value: unknown, now: Date = new Date()): string | null {
  const at = toDate(value);
  if (at === null) return null;

  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const days = calendarDaysBetween(at, now);

  if (days === 0) return `today at ${time}`;
  if (days === 1) return `yesterday at ${time}`;
  if (days > 1 && days < 7) return `${at.toLocaleDateString(undefined, { weekday: 'long' })} at ${time}`;
  // A future instant is not a state this page can produce, but a clock skew between the server and
  // the reader's device can fake one by a few minutes, and "in -0 days" would be nonsense.
  if (days < 0) return time;
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Just the date, for "playing here since": a join date has no useful time of day. */
export function friendlyDate(value: unknown): string | null {
  const at = toDate(value);
  if (at === null) return null;
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The machine-readable original, for a `<time dateTime>` attribute. */
export function isoOf(value: unknown): string | null {
  const at = toDate(value);
  return at === null ? null : at.toISOString();
}

function toDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

/**
 * Whole calendar days between two instants in the READER's timezone, which is what "yesterday"
 * means to a person. Subtracting epoch millis and dividing by 86400000 would call 23:50 last night
 * "0 days ago" at 00:10 this morning.
 */
function calendarDaysBetween(at: Date, now: Date): number {
  const startOf = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.round((startOf(now) - startOf(at)) / 86_400_000);
}
